/**
 * Policy Management API Client
 * 
 * Handles all API calls for agent policy configuration
 */
import { BACKEND_URL, API } from '@/lib/backendUrl';

const API_BASE_URL = BACKEND_URL || '';

/**
 * Get complete policy for a hotel
 */
export const getHotelPolicy = async (hotelId) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch hotel policy');
  }
  return response.json();
};

/**
 * Get rules for a specific agent
 */
export const getAgentRules = async (hotelId, agentName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${agentName} rules`);
  }
  return response.json();
};

/**
 * Update agent rules
 */
export const updateAgentRules = async (hotelId, agentName, updates) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      agent_name: agentName,
      ...updates
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update agent rules');
  }
  return response.json();
};

/**
 * Add tool to allowed list
 */
export const addAllowedTool = async (hotelId, agentName, toolName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}/allow-tool`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tool_name: toolName }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to add allowed tool');
  }
  return response.json();
};

/**
 * Remove tool from allowed list
 */
export const removeAllowedTool = async (hotelId, agentName, toolName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}/remove-allowed-tool`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tool_name: toolName }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to remove allowed tool');
  }
  return response.json();
};

/**
 * Add tool to denied list
 */
export const addDeniedTool = async (hotelId, agentName, toolName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}/deny-tool`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tool_name: toolName }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to add denied tool');
  }
  return response.json();
};

/**
 * Set daily limit for a tool
 */
export const setToolDailyLimit = async (hotelId, agentName, toolName, dailyLimit) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}/set-tool-limit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tool_name: toolName,
      daily_limit: dailyLimit
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to set tool limit');
  }
  return response.json();
};

/**
 * Enable agent
 */
export const enableAgent = async (hotelId, agentName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}/enable`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to enable ${agentName}`);
  }
  return response.json();
};

/**
 * Disable agent
 */
export const disableAgent = async (hotelId, agentName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/agent/${agentName}/disable`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to disable ${agentName}`);
  }
  return response.json();
};

/**
 * Reset policy to defaults
 */
export const resetPolicyToDefaults = async (hotelId) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/policies/hotel/${hotelId}/reset-defaults`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    throw new Error('Failed to reset policy to defaults');
  }
  return response.json();
};

/**
 * Get agent policy summary (for checking what's allowed)
 */
export const getAgentPolicySummary = async (hotelId, agentName) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/agents/${hotelId}/${agentName}/policy`);
  if (!response.ok) {
    throw new Error('Failed to fetch policy summary');
  }
  return response.json();
};
